/**
 * LoginForm
 * Calls onSuccess(session) WITHOUT setting the session.
 * The parent (LoginPage) holds the pending session and only
 * commits it after the disclaimer is accepted.
 */
import { useState } from 'react'
import { sha256, workerPostV1 } from '../../api/worker'
import type { FomSession } from '../../stores/auth'

interface LoginResponse {
  success: boolean
  rank?: string
  employeeNo?: string
  token?: string
  refreshToken?: string
  expiresIn?: number
  name?: string
  email?: string
  phone?: string
  message?: string
  error?: string
}

interface Props {
  onSuccess: (session: FomSession) => void
  onShowRegister: () => void
  onShowReset: () => void
}

export default function LoginForm({ onSuccess, onShowRegister, onShowReset }: Props) {
  const [callSign, setCallSign] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleLogin() {
    if (!callSign.trim() || !password) { setError('Enter call sign and password'); return }
    setLoading(true); setError('')
    try {
      const hash = await sha256(password)
      const res = await workerPostV1<LoginResponse>('/api/v1/auth/login', {
        callSign: callSign.trim().toUpperCase(),
        passwordHash: hash,
      })
      if (res.success) {
        // Build session but do NOT call setSession — parent handles it after disclaimer
        const session: FomSession = {
          callSign: callSign.trim().toUpperCase(),
          rank: res.rank ?? '',
          employeeNo: res.employeeNo ?? '',
          token: res.token ?? '',
          refreshToken: res.refreshToken ?? '',
          expiresAt: res.expiresIn ? Date.now() + res.expiresIn * 1000 : undefined,
          name: res.name ?? '',
          email: res.email ?? '',
          phone: res.phone ?? '',
        }
        onSuccess(session)
      } else {
        const msg = res.message ?? res.error ?? 'Login failed'
        if (msg.includes('Incorrect password')) setError('Incorrect password. Please try again or reset your password.')
        else if (msg.includes('not found')) setError('Call sign not recognized. Check your call sign or create an account.')
        else if (msg.includes('pending')) setError('Account pending approval. Please wait for a supervisor to approve your registration.')
        else setError(msg)
      }
    } catch {
      setError('Network error. Check your connection and try again.')
    }
    setLoading(false)
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    background: 'var(--bg-deep)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '10px 12px',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none', marginBottom: 10,
  }

  return (
    <div>
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#ef4444', marginBottom: 12 }}>
          {error}
          {error.includes('reset') && (
            <> — <span style={{ color: '#f59e0b', cursor: 'pointer', fontWeight: 700 }} onClick={onShowReset}>Reset password</span></>
          )}
        </div>
      )}
      <input value={callSign} onChange={e => setCallSign(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()}
        placeholder="Call sign (e.g. S-01)" autoCapitalize="characters" style={inputStyle} />
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="Password"
          style={{ ...inputStyle, marginBottom: 0, paddingRight: 40 }} />
        <button type="button" onClick={() => setShowPw(v => !v)}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>
          {showPw ? '🙈' : '👁️'}
        </button>
      </div>
      <button onClick={handleLogin} disabled={loading} style={{
        width: '100%', padding: 12, background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
        border: 'none', borderRadius: 8, color: '#1a2744', fontWeight: 700,
        fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1,
      }}>
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} onClick={onShowReset}>Forgot password?</span>
        {' · '}
        <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} onClick={onShowRegister}>Create account</span>
        {(!window.location.hostname.includes('castellan-security.com') || window.location.hostname === 'castellan-security.com') && (
          <>{' · '}<span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} onClick={() => window.location.href = '/#/signup'}>New organization?</span></>
        )}
      </div>
    </div>
  )
}
